// deps
import {
  Box,
  Icon,
  InputGroup,
  InputRightElement,
  useColorModeValue,
} from "@chakra-ui/react";
import { getLocalTimeZone, today } from "@internationalized/date";
import { IoBanOutline, IoCalendarOutline } from "react-icons/io5";
import DateField from "./DateField";
import FieldButton from "./FieldButton";
import StyledField from "./StyledField";
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui";

/**
 * @typedef Props
 * @property {boolean} isOpen
 * Component is used by both datepicker and date range picker, so state have both types
 * @property {import("@react-stately/datepicker").DatePickerState | import("@react-stately/datepicker").DateRangePickerState} state
 * @property {() => void} onClose
 * @property {import("react").FunctionComponentElement} children
 * @property {(value: any) => void} [setFocusedDate]
 * @property {import("@react-aria/datepicker").AriaDatePickerProps<import("@internationalized/date").DateValue>} [fieldProps]
 * @property {boolean} isDateRangePicker
 * @property {boolean} isDisabled
 * @property {import("react").DOMAttributes<any>} groupProps
 * @property {import("react").MutableRefObject<null>} [inputGroupRef]
 * @property {any} [startFieldProps]
 * @property {any} [endFieldProps]
 * @property {(event: any) => void} handleFocus
 * @property {import("@react-aria/button").AriaButtonProps<"button">} buttonProps
 */
/**
 * @param {Props} props
 * @returns {import("react").FunctionComponentElement<Props>}
 */
export default function PopoverContainer(props) {
  let { isOpen, state, onClose, children, setFocusedDate, fieldProps } = props;

  const borderColor = useColorModeValue("gray.300", "gray.600");

  /**
   * @param {import("@internationalized/date").CalendarDate} date
   */
  function onDateFieldChange(date) {
    // Check if the date is valid
    if (date && !isNaN(date.toDate("UTC").getTime())) {
      fieldProps?.onChange?.(date);
      setFocusedDate?.(date);
    } else {
      // If the date is invalid, set the field value to an empty string to clear the field (happens when using backspace to remove date)
      // @ts-ignore
      props.fieldProps?.onChange?.("");
      setFocusedDate?.(today(getLocalTimeZone()));
    }
  }

  return (
    <Popover
      strategy="fixed"
      placement="bottom"
      isOpen={isOpen}
      onClose={onClose}>
      <InputGroup
        {...props.groupProps}
        ref={props.inputGroupRef}
        width="auto"
        display="inline-flex"
        onFocus={props.handleFocus}>
        {props.isDateRangePicker ? (
          <StyledField isOpen={state.isOpen}>
            <DateField {...props.startFieldProps} />
            <Box as="span" aria-hidden="true" paddingX="2">
              {"–"}
            </Box>
            <DateField {...props.endFieldProps} />
            {state.validationState === "invalid" && (
              <Icon
                as={IoBanOutline}
                color="red.600"
                position="absolute"
                right="12"
              />
            )}
          </StyledField>
        ) : (
          <StyledField pr="4.5rem" w="100%" isOpen={state.isOpen}>
            <DateField {...props.fieldProps} onChange={onDateFieldChange} />
            {state.validationState === "invalid" && (
              <Icon
                as={IoBanOutline}
                color="red.600"
                position="absolute"
                right="12"
              />
            )}
          </StyledField>
        )}
        <PopoverTrigger>
          <InputRightElement zIndex="initial">
            <FieldButton {...props.buttonProps} isDisabled={props.isDisabled}>
              <Icon as={IoCalendarOutline} />
            </FieldButton>
          </InputRightElement>
        </PopoverTrigger>
      </InputGroup>

      <PopoverContent
        width={props.isDateRangePicker ? "100%" : "auto"}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md">
        <PopoverBody p="1rem">{children}</PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
